// The Help page is the server's chainlit.md, split into sections.
//
// One English markdown file is the only source: the mobile app shows the same
// text in its Readme sheet. That app opens every link it finds in the text,
// and a relative link goes nowhere there, so the file carries no links at all.
// The web page adds them instead: a table of contents from the `## ` headings,
// and an "Open ..." button on the sections that describe a page of the app.

export interface DocSection {
  id: string; // anchor, from the heading
  title: string;
  body: string; // markdown under the heading
}

export interface Doc {
  title: string;
  intro: string;
  sections: DocSection[];
}

export function slug(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'section'
  );
}

/** `# Title`, an intro, then one section per `## ` heading. Headings inside
 *  fenced code are not headings. */
export function parseDoc(markdown: string): Doc {
  const lines = (markdown || '').replace(/\r\n/g, '\n').split('\n');
  let title = '';
  const intro: string[] = [];
  const sections: DocSection[] = [];
  let current: { title: string; lines: string[] } | null = null;
  let fenced = false;

  for (const line of lines) {
    if (/^\s*```/.test(line)) fenced = !fenced;
    if (!fenced && !title && !current && /^# /.test(line)) {
      title = line.slice(2).trim();
      continue;
    }
    if (!fenced && /^## /.test(line)) {
      if (current) sections.push(finish(current, sections));
      current = { title: line.slice(3).trim(), lines: [] };
      continue;
    }
    (current ? current.lines : intro).push(line);
  }
  if (current) sections.push(finish(current, sections));
  return { title, intro: intro.join('\n').trim(), sections };
}

function finish(
  s: { title: string; lines: string[] },
  done: DocSection[]
): DocSection {
  let id = slug(s.title);
  // Two sections with one title still need two anchors.
  for (let n = 2; done.some((d) => d.id === id); n++) id = `${slug(s.title)}-${n}`;
  return { id, title: s.title, body: s.lines.join('\n').trim() };
}

export interface FeatureLink {
  label: string;
  path: string;
  /** Feature flag the page needs; the button is hidden without it. */
  flag?: string;
}

/** Sections that describe a page of the app, by heading anchor. A heading
 *  not listed here simply has no button. */
export const FEATURE_LINKS: Record<string, FeatureLink> = {
  'asking-a-question': { label: 'Open Chat', path: '/' },
  'skills-ready-made-reports': { label: 'Open Chat', path: '/' },
  dashboard: { label: 'Open Dashboard', path: '/dashboard', flag: 'dashboard' },
  anomalies: {
    label: 'Open Anomalies',
    path: '/anomalies',
    flag: 'anomaly_detection'
  },
  'external-signals': { label: 'Open External Signals', path: '/external-signals' },
  notifications: {
    label: 'Open Notifications',
    path: '/notifications',
    flag: 'notices'
  },
  memory: { label: 'Open Signals memory', path: '/memory', flag: 'tenant_memory' }
};

export function linkFor(
  id: string,
  features: Record<string, boolean | undefined>
): FeatureLink | null {
  const link = FEATURE_LINKS[id];
  if (!link) return null;
  if (link.flag && !features[link.flag]) return null;
  return link;
}
